/**
 * Enhanced Clicky Menus v2.0.0 - 3-Level Navigation Support
 * Based on Clicky Menus v1.5.0 with Sara Soueidan accessibility patterns
 */

(function() {
    'use strict';

    const ClickyMenus = function(menu, menuIndex) {
        // DOM element(s)
        const container = menu.parentElement;
        let currentMenuItem = null;
        let currentSubMenuItem = null;
        let i = 0;
        let len;

        this.init = function() {
            menuSetup();
            document.addEventListener('click', closeIfClickOutsideMenu);
            document.addEventListener('keydown', handleGlobalKeydown);
            menu.addEventListener('clickyMenusClose', closeAllSubmenus);
        };

        /*===================================================
        =            Enhanced Menu Setup for 3 Levels      =
        ===================================================*/
        function menuSetup() {
            menu.classList.remove('no-js');
            menu.classList.add('enhanced');
            
            const submenuSelector = 'clickySubmenuSelector' in menu.dataset ? 
                menu.dataset.clickySubmenuSelector : 'ul';

            // Handle all submenus at any level
            menu.querySelectorAll('[data-has-children]').forEach((menuItem) => {
                const submenu = menuItem.querySelector(':scope > ul');
                const level = getMenuLevel(menuItem);
                
                if (submenu) {
                    const button = convertLinkToButton(menuItem);
                    setUpAria(submenu, button, level);
                    
                    // Bind event listeners
                    button.addEventListener('click', function(e) {
                        e.preventDefault();
                        toggleSubmenu(button, submenu, level);
                    });
                    
                    button.addEventListener('keydown', function(e) {
                        handleButtonKeydown(e, button, submenu, level);
                    });
                    
                    // Add keyboard navigation to submenu items
                    addSubmenuKeyboardNavigation(submenu, button, level);
                }
            });
        }

        /*===================================================
        =            Level Detection & Management           =
        ===================================================*/
        function getMenuLevel(element) {
            let level = 1;
            let parent = element.parentElement;
            
            while (parent && !parent.classList.contains('clicky-menu')) {
                if (parent.tagName === 'UL') {
                    level++;
                }
                parent = parent.parentElement;
            }
            
            return level;
        }

        /*===================================================
        =            Enhanced Toggle Functions              =
        ===================================================*/
        function toggleSubmenu(button, submenu, level) {
            const isExpanded = button.getAttribute('aria-expanded') === 'true';
            
            if (level === 1) {
                // Close other top-level menus
                if (currentMenuItem && button !== currentMenuItem) {
                    closeSubmenu(currentMenuItem);
                }
                
                if (isExpanded) {
                    closeSubmenu(button);
                    currentMenuItem = null;
                } else {
                    openSubmenu(button, submenu);
                    currentMenuItem = button;
                }
            } else if (level === 2) {
                // Handle second-level menus
                if (currentSubMenuItem && button !== currentSubMenuItem) {
                    closeSubmenu(currentSubMenuItem);
                }
                
                if (isExpanded) {
                    closeSubmenu(button);
                    currentSubMenuItem = null;
                } else {
                    openSubmenu(button, submenu);
                    currentSubMenuItem = button;
                }
            }
        }

        function openSubmenu(button, submenu) {
            button.setAttribute('aria-expanded', 'true');
            submenu.removeAttribute('hidden');
            submenu.setAttribute('aria-hidden', 'false');
            
            // Only apply off-screen detection to top-level menus
            const level = getMenuLevel(button.parentElement);
            if (level === 1) {
                preventOffScreenSubmenu(submenu);
            }
        }

        function closeSubmenu(button) {
            const submenu = document.getElementById(button.getAttribute('aria-controls'));
            if (submenu) {
                button.setAttribute('aria-expanded', 'false');
                submenu.setAttribute('hidden', '');
                submenu.setAttribute('aria-hidden', 'true');
                
                // Close any nested submenus
                const nestedButtons = submenu.querySelectorAll('button[aria-expanded="true"]');
                nestedButtons.forEach(nestedButton => {
                    closeSubmenu(nestedButton);
                });
            }
        }

        function closeAllSubmenus() {
            const allButtons = menu.querySelectorAll('button[aria-expanded="true"]');
            allButtons.forEach(button => {
                closeSubmenu(button);
            });
            currentMenuItem = null;
            currentSubMenuItem = null;
        }

        /*===================================================
        =            Enhanced Keyboard Navigation           =
        ===================================================*/
        function handleButtonKeydown(e, button, submenu, level) {
            const key = e.key;
            
            switch (key) {
                case 'Enter':
                case ' ':
                    e.preventDefault();
                    toggleSubmenu(button, submenu, level);
                    break;
                    
                case 'ArrowDown':
                    e.preventDefault();
                    if (button.getAttribute('aria-expanded') !== 'true') {
                        openSubmenu(button, submenu);
                    }
                    focusFirstMenuItem(submenu);
                    break;
                    
                case 'ArrowRight':
                    if (level === 1) {
                        e.preventDefault();
                        if (button.getAttribute('aria-expanded') !== 'true') {
                            openSubmenu(button, submenu);
                        }
                        focusFirstMenuItem(submenu);
                    }
                    break;
                    
                case 'ArrowLeft':
                    if (level === 2) {
                        e.preventDefault();
                        closeSubmenu(button);
                        const parentButton = findParentButton(button);
                        if (parentButton) {
                            parentButton.focus();
                        }
                    }
                    break;
                    
                case 'Escape':
                    e.preventDefault();
                    closeSubmenu(button);
                    button.focus();
                    break;
            }
        }

        function addSubmenuKeyboardNavigation(submenu, parentButton, level) {
            const menuItems = submenu.querySelectorAll(':scope > li > a, :scope > li > button');
            
            menuItems.forEach((item, index) => {
                item.addEventListener('keydown', function(e) {
                    handleMenuItemKeydown(e, item, menuItems, index, submenu, parentButton, level);
                });
            });
        }

        function handleMenuItemKeydown(e, item, menuItems, index, submenu, parentButton, level) {
            const key = e.key;
            
            switch (key) {
                case 'ArrowDown':
                    e.preventDefault();
                    const nextIndex = (index + 1) % menuItems.length;
                    menuItems[nextIndex].focus();
                    break;
                    
                case 'ArrowUp':
                    e.preventDefault();
                    const prevIndex = (index - 1 + menuItems.length) % menuItems.length;
                    menuItems[prevIndex].focus();
                    break;
                    
                case 'ArrowRight':
                    // If this item has children, open its submenu
                    const hasChildren = item.parentElement.hasAttribute('data-has-children');
                    if (hasChildren && item.tagName === 'BUTTON') {
                        e.preventDefault();
                        const childSubmenu = item.parentElement.querySelector('ul');
                        if (childSubmenu) {
                            openSubmenu(item, childSubmenu);
                            focusFirstMenuItem(childSubmenu);
                        }
                    }
                    break;
                    
                case 'ArrowLeft':
                    e.preventDefault();
                    if (level === 2) {
                        closeSubmenu(parentButton);
                        parentButton.focus();
                    } else if (level === 3) {
                        const grandParentButton = findParentButton(parentButton);
                        if (grandParentButton) {
                            closeSubmenu(parentButton);
                            parentButton.focus();
                        }
                    }
                    break;
                    
                case 'Escape':
                    e.preventDefault();
                    closeSubmenu(parentButton);
                    parentButton.focus();
                    break;
                    
                case 'Home':
                    e.preventDefault();
                    menuItems[0].focus();
                    break;
                    
                case 'End':
                    e.preventDefault();
                    menuItems[menuItems.length - 1].focus();
                    break;
                    
                case 'Tab':
                    // Allow natural tab flow, but close menus if tabbing out of navigation
                    if (!e.shiftKey && index === menuItems.length - 1) {
                        setTimeout(() => {
                            if (!menu.contains(document.activeElement)) {
                                closeAllSubmenus();
                            }
                        }, 0);
                    }
                    break;
            }
        }

        function handleGlobalKeydown(e) {
            // Close all menus on Escape if focus is anywhere in navigation
            if (e.key === 'Escape' && menu.contains(document.activeElement)) {
                closeAllSubmenus();
                // Focus the first top-level menu item
                const firstTopLevelItem = menu.querySelector('li > a, li > button');
                if (firstTopLevelItem) {
                    firstTopLevelItem.focus();
                }
            }
        }

        /*===================================================
        =            Helper Functions                       =
        ===================================================*/
        function focusFirstMenuItem(submenu) {
            const firstItem = submenu.querySelector(':scope > li > a, :scope > li > button');
            if (firstItem) {
                firstItem.focus();
            }
        }

        function findParentButton(element) {
            let parent = element.parentElement;
            while (parent && !parent.classList.contains('clicky-menu')) {
                if (parent.tagName === 'LI' && parent.parentElement.tagName === 'UL') {
                    const parentLi = parent.parentElement.parentElement;
                    if (parentLi.tagName === 'LI') {
                        return parentLi.querySelector(':scope > button');
                    }
                }
                parent = parent.parentElement;
            }
            return null;
        }

        function preventOffScreenSubmenu(submenu) {
            const screenWidth = window.innerWidth ||
                document.documentElement.clientWidth ||
                document.body.clientWidth;
            const parent = submenu.offsetParent;
            const menuLeftEdge = parent.getBoundingClientRect().left;
            const menuRightEdge = menuLeftEdge + submenu.offsetWidth;

            if (menuRightEdge + 32 > screenWidth) {
                submenu.classList.add('sub-menu--right');
            }
        }

        function closeIfClickOutsideMenu(e) {
            if ((currentMenuItem || currentSubMenuItem) && !e.target.closest('#' + container.id)) {
                closeAllSubmenus();
            }
        }

        /*===================================================
        =            Enhanced DOM Manipulation              =
        ===================================================*/
        function convertLinkToButton(menuItem) {
            const link = menuItem.querySelector(':scope > a');
            if (!link) return null;
            
            const linkHTML = link.innerHTML;
            const linkAtts = link.attributes;
            const button = document.createElement('button');

            // Copy button attributes and content from link
            button.innerHTML = linkHTML.trim();
            for (let i = 0; i < linkAtts.length; i++) {
                const attr = linkAtts[i];
                if (attr.name !== 'href') {
                    button.setAttribute(attr.name, attr.value);
                }
            }

            menuItem.replaceChild(button, link);
            return button;
        }

        function setUpAria(submenu, button, level) {
            const submenuId = submenu.getAttribute('id');
            let id;
            const idSuffix = `-submenu-${menuIndex}-${i}`;
            
            if (!submenuId) {
                const buttonText = button.textContent.trim().replace(/\s+/g, '-')
                    .replace(/^[^a-zA-Z]+|[^\w:.-]+/g, "").toLowerCase();
                id = buttonText + idSuffix;
                i++;
            } else {
                id = submenuId + idSuffix;
                i++;
            }

            // Set button ARIA
            button.setAttribute('aria-controls', id);
            button.setAttribute('aria-expanded', 'false');
            button.setAttribute('aria-haspopup', 'true');
            
            // Add level context for screen readers
            const levelText = level === 1 ? 'main menu' : level === 2 ? 'submenu' : 'sub-submenu';
            const existingLabel = button.getAttribute('aria-label') || '';
            if (!existingLabel.includes(levelText)) {
                button.setAttribute('aria-label', `${button.textContent.trim()}, ${levelText}`);
            }

            // Set submenu ARIA
            submenu.setAttribute('id', id);
            submenu.setAttribute('aria-hidden', 'true');
            submenu.setAttribute('hidden', '');
            submenu.setAttribute('role', 'menu');
            
            // Set menu item roles for screen readers
            const menuItems = submenu.querySelectorAll(':scope > li');
            menuItems.forEach(item => {
                const link = item.querySelector('a');
                if (link && !item.hasAttribute('data-has-children')) {
                    item.setAttribute('role', 'menuitem');
                }
            });
        }
    };

    /* Initialize enhanced clicky menus */
    document.addEventListener('DOMContentLoaded', function() {
        const menus = document.querySelectorAll('.clicky-menu');
        menus.forEach((menu, index) => {
            const clickyMenu = new ClickyMenus(menu, index + 1);
            clickyMenu.init();
        });
    });

/* External menu close functionality */
    function dispatchMenuClose(e) {
        const menuId = e.currentTarget.getAttribute('data-clicky-menus-close');
        const menu = document.getElementById(menuId);
        if (menu) {
            menu.dispatchEvent(new Event('clickyMenusClose'));
        }
    }

    const menuClosers = document.querySelectorAll('[data-clicky-menus-close]');
    if (menuClosers.length > 0) {
        menuClosers.forEach((menuCloser) => {
            menuCloser.addEventListener('click', dispatchMenuClose);
        });
    }

    // Mobile Navigation Enhancement
    const nav = document.getElementById("site-nav");
    const navContent = nav.querySelector('.clicky-menu');
    const isDesktop = window.matchMedia("(min-width: 52em)");
    let navButton = null;

    function createMobileButton() {
        if (navButton) return; // Don't create if already exists
        
        navButton = document.createElement('button');
        navButton.className = 'nav-toggle';
        navButton.id = 'nav-toggle';
        navButton.setAttribute('aria-expanded', 'false');
        
        // Add hamburger icon and text
        navButton.innerHTML = `
            <svg height="21" viewBox="0 0 24 24" width="24" aria-hidden="true" style="color: inherit;">
                <path d="M3 13h18c0.552 0 1-0.448 1-1s-0.448-1-1-1h-18c-0.552 0-1 0.448-1 1s0.448 1 1 1zM3 7h18c0.552 0 1-0.448 1-1s-0.448-1-1-1h-18c-0.552 0-1 0.448-1 1s0.448 1 1 1zM3 19h18c0.552 0 1-0.448 1-1s-0.448-1-1-1h-18c-0.552 0-1 0.448-1 1s0.448 1 1 1z" fill="currentColor"></path>
            </svg>
            Navigation
        `;

        // Add nav-content class to existing ul
        if (navContent) {
            navContent.classList.add('nav-content');
        }

        // Insert mobile button at the beginning of nav
        nav.insertBefore(navButton, navContent);

        // Add click listener
        navButton.addEventListener('click', toggleNavigationContent);
    }

    function removeMobileButton() {
        if (navButton) {
            navButton.remove();
            navButton = null;
        }
    }

    function toggleNavigationContent(e) {
        const dropdowns = nav.querySelectorAll('[data-has-children] > ul');
        
        if (navButton.getAttribute('aria-expanded') === 'true') {
            navButton.setAttribute('aria-expanded', 'false');
            
            // Collapse all dropdowns when navigation is hidden
            dropdowns.forEach(function(dropdown) {
                dropdown.setAttribute('hidden', '');
                const btn = dropdown.parentNode.querySelector('button');
                if (btn) {
                    btn.setAttribute('aria-expanded', 'false');
                }
            });
        } else {
            navButton.setAttribute('aria-expanded', 'true');
        }
    }

    function handleResize(mediaQuery) {
        if (mediaQuery.matches) {
            // Desktop view - remove button, show nav
            removeMobileButton();
            if (navContent) {
                navContent.removeAttribute('hidden');
            }
        } else {
            // Mobile view - create button, manage nav visibility
            createMobileButton();
            if (navButton) {
                navButton.setAttribute('aria-expanded', 'false');
            }
        }
    }

    // Handle responsive behavior - check initial state
    handleResize(isDesktop);
    isDesktop.addEventListener('change', handleResize);

})();